import Vue from 'vue';
import {TaxReturn, TaxReturnLine, TaxReturnForm} from '../models/tax-return';
import {SpecialCategories} from '@/store/modules/uploads';

const state = () => ({
   taxReturns: {},
   taxReturnLines: {},
   taxReturnData: {},
   taxReturnPeriods: [],
   taxReturnForms: [],
   taxReturnUploadCategoryId: null,
   missingTaxReturns: [],
});

const getters = {
   // Tax Returns
   taxReturns: (state) => Object.values(state.taxReturns),

   // Tax Return Lines
   taxReturnLines: (state) => Object.values(state.taxReturnLines),

   // Tax Return Data
   taxReturnData: (state) => Object.values(state.taxReturnData),
   taxReturnPeriods: (state) => state.taxReturnPeriods,

   // Tax Return Forms
   taxReturnForms: (state) => Object.values(state.taxReturnForms),

   /** The tax return UploadCategory */
   taxReturnUploadCategory: (state, getters, rootState, rootGetters) => {
      if (state.taxReturnUploadCategoryId === null) {
         return null;
      }
      return rootGetters['uploads/uploadCategoryMap'][state.taxReturnUploadCategoryId];
   },

   taxReturnFiles: (state, getters) => {
      if (getters.taxReturnUploadCategory === null) {
         return [];
      }

      const category = getters.taxReturnUploadCategory;
      const rawSummary = category._summary || category.summary;

      if (!rawSummary) {
         return [];
      }

      return Array.isArray(rawSummary) ? rawSummary : Object.values(rawSummary);
   },

   missingTaxReturns: (state) => {
      return state.missingTaxReturns;
   },
};

const mutations = {
   // Clear all data
   clear: (state) => {
      state.taxReturns = {};
      state.taxReturnLines = {};
      state.taxReturnData = {};
      state.taxReturnPeriods = [];
      state.taxReturnUploadCategoryId = null;
      state.taxReturnForms = [];
   },

   // Tax Return mutations
   setTaxReturns: (state, {taxReturns}) => {
      taxReturns.forEach((taxReturnData) => {
         const taxReturn = new TaxReturn(taxReturnData);
         Vue.set(state.taxReturns, taxReturn.id, taxReturn);
      });
   },

   // Tax Return Line mutations
   setTaxReturnLines: (state, {taxReturnLines}) => {
      taxReturnLines.forEach((lineData) => {
         const line = new TaxReturnLine(lineData);
         Vue.set(state.taxReturnLines, line.id, line);
      });
   },

   /** Store the tax return file upload category id */
   setTaxReturnUploadCategoryId(state, {uploadCategoryId}) {
      state.taxReturnUploadCategoryId = uploadCategoryId;
   },

   setTaxReturnData(state, {taxReturnData}) {
      state.taxReturnData = {};
      taxReturnData.items.forEach((item) => {
         Vue.set(state.taxReturnData, `${item.taxReturnLineId}-${item.periodId}`, item);
      });
      state.taxReturnPeriods = taxReturnData.periods || [];
   },

   setTaxReturnForms(state, {taxReturnFormsData}) {
      if (!taxReturnFormsData) {
         return;
      }
      state.taxReturnForms = [];
      taxReturnFormsData.forEach((item) => {
         const taxReturnFormData = new TaxReturnForm(item);
         Vue.set(state.taxReturnForms, taxReturnFormData.id, taxReturnFormData);
      });
   },

   patchTaxReturnData(state, {lineId, periodId, value, item}) {
      Vue.set(state.taxReturnData, `${lineId}-${periodId}`, {
         ...item,
         value: value,
      });
   },

   setMissingTaxReturns(state, {missingTaxReturns}) {
      state.missingTaxReturns = missingTaxReturns;
   },
};

const actions = {
   // Load actions that fetch and commit to store
   async loadTaxReturns({commit}, {companyId, periodId}) {
      const taxReturns = (
         await this._vm.$http.get(
            `/api/tax_return?company_id=${companyId}${periodId ? `&speriod_id=${periodId}` : ''}`
         )
      ).data;
      commit('setTaxReturns', {taxReturns});
   },

   async loadTaxReturnLines({commit}, {companyId, periodId}) {
      const taxReturnLines = (
         await this._vm.$http.get(
            `/api/tax_return_line?company_id=${companyId}${
               periodId ? `&speriod_id=${periodId}` : ''
            }`
         )
      ).data;
      commit('setTaxReturnLines', {taxReturnLines});
      return taxReturnLines;
   },

   // Load actions that fetch and commit to store
   async loadTaxReturnData({commit}, {companyId, periodId, studyId}) {
      const query = [
         periodId ? `speriod_id=${periodId}` : null,
         studyId ? `study_id=${studyId}` : null,
      ]
         .filter(Boolean)
         .join('&');
      const taxReturnData = (
         await this._vm.$http.get(`/api/company/${companyId}/tax_return${query ? `?${query}` : ''}`)
      ).data;
      commit('setTaxReturnData', {taxReturnData});
   },

   async updateTaxReturnItem(_, {periodId, lineId, value}) {
      return (
         await this._vm.$http.put(`/api/speriod/${periodId}/line/${lineId}/tax_return_line_item`, {
            value,
         })
      ).data;
   },

   updateTaxReturnData({commit, rootGetters}, {lineId, periodId, value}) {
      const item = rootGetters['taxReturn/taxReturnData'].find(
         (item) => item.taxReturnLineId == lineId && item.periodId == periodId
      );
      commit('patchTaxReturnData', {item, value, lineId, periodId});
   },

   // File operations
   async fetchFiles({commit, dispatch}, {companyId}) {
      const uploadCategory = await dispatch(
         'uploads/loadSpecialCategory',
         {
            companyId,
            category: SpecialCategories.TAX_RETURN,
         },
         {root: true}
      );
      commit('setTaxReturnUploadCategoryId', {
         uploadCategoryId: uploadCategory.id,
      });
   },

   /** Delete a tax return file */
   async deleteTaxReturnFile({state, dispatch}, {fileId, force = true}) {
      await dispatch(
         'uploads/deleteFile',
         {
            fileId,
            uploadCategoryId: state.taxReturnUploadCategoryId,
            force,
         },
         {root: true}
      );
   },

   /** Upload tax return files */
   async uploadTaxReturnFiles({state, dispatch}, {taxReturnId, files, periodId, studyId}) {
      if (files.length === 0) {
         return;
      }

      const validation = {
         type: SpecialCategories.TAX_RETURN,
         data: {
            tax_return_id: taxReturnId,
         },
      };

      await dispatch(
         'uploads/uploadFiles',
         {
            uploadCategoryId: state.taxReturnUploadCategoryId,
            files,
            validation,
            periodId,
            studyId,
         },
         {root: true}
      );
   },

   async loadTaxReturnForms({commit}, {category}) {
      if (!category) {
         return;
      }

      const params = {state: category};

      const taxReturnForms = (await this._vm.$http.get(`/api/tax_return_with_lines`, {params}))
         .data;
      commit('setTaxReturnForms', {taxReturnFormsData: taxReturnForms});
   },

   async saveTaxReturnForm(_, {taxReturnForm, category}) {
      if (!taxReturnForm || !category) {
         return;
      }

      const taxReturnFormPayload = {
         tax_return: {
            form_name: taxReturnForm.taxReturn.name,
            state: category,
            entityType: taxReturnForm.taxReturn.entityType,
         },
         tax_return_lines: taxReturnForm.taxReturnLines.map((line) => {
            return {line_name: line.name, line_id: line.id};
         }),
      };

      if (taxReturnForm.id) {
         return (
            await this._vm.$http.put(
               `/api/tax_return_with_lines/tax_return_id/${taxReturnForm.id}`,
               taxReturnFormPayload
            )
         ).data;
      } else {
         return (await this._vm.$http.post(`/api/tax_return_with_lines`, taxReturnFormPayload))
            .data;
      }
   },

   async deleteTaxReturnForm(_, {taxReturnFormId}) {
      const params = {force: true};
      return await this._vm.$http.delete(`/api/tax_return/${taxReturnFormId}`, {params});
   },

   async loadMissingTaxReturns({commit}) {
      const response = await this._vm.$http.get(`/api/nouploadpdfs`);
      commit('setMissingTaxReturns', {missingTaxReturns: response.data});
   },
};

export default {
   namespaced: true,
   state,
   getters,
   mutations,
   actions,
};
